import React, { useState, useEffect } from 'react';
import {
  ResultsData,
  SearchViewModel,
  SortOptions,
} from '../../models/search-model';
import { searchSuggestion, searchv2 } from '~/common/services/search-servicev2';
import {
  AIPreviewRequest,
  AIPreviewResponse,
  AIPreviewError,
  AIPreviewState,
  AIModeSeed,
} from '../ai-preview/types';

type SearchProviderContext = {
  searchTerm: string;
  contentSearchResults: ResultsData | null;
  selectedFilters: string[] | null;
  selectedPage?: string | null;
  selectedSort: SortOptions;
  isSearchLoading: boolean;
  searchData: (
    query: string,
    selectedFilter: string[] | null,
    selectedPage: string | null,
    selectedSort: SortOptions,
    newPage: boolean
  ) => Promise<ResultsData>;
  isNewPage: boolean;
  getSuggestions: (keyword: string) => Promise<string[]>;
  adsData: SearchViewModel;

  // AI Preview state management
  aiPreviewState: AIPreviewState;
  aiPreviewVisible: boolean;
  aiPreviewResponse: AIPreviewResponse | null;
  aiPreviewError: AIPreviewError | null;
  aiPreviewSessionId: string | null;

  // AI Preview methods
  triggerAIPreview: (query: string) => Promise<void>;
  hideAIPreview: () => void;
  clearAIPreviewError: () => void;
  resetAIPreview: () => void;

  // AI answering mode (CMS-gated): overview + conversational AI Mode view
  aiEnabled: boolean;
  // Whether the conversational half (AI Mode tab + thread) is available —
  // false in simple AI mode, where the overview answers once.
  aiConversational: boolean;
  aiModeActive: boolean;
  aiModeSeed: AIModeSeed | null;
  openAIMode: (seed?: AIModeSeed | null) => void;
  resumeAIMode: () => void;
  closeAIMode: () => void;
  completeAIPreview: (response: AIPreviewResponse) => void;
};

export const SearchProvider = React.createContext<SearchProviderContext | null>(
  null
);

export const useSearchStore = (): SearchProviderContext => {
  const context = React.useContext(SearchProvider);
  if (!context) {
    throw new Error('useSearchStore must be used within a SearchProvider');
  }
  return context;
};

export const SearchStore: React.FC<SearchViewModel> = (props) => {
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [contentSearchResults, setContentSearchResults] =
    useState<ResultsData | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[] | null>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [isNewPage, setIsNewPage] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<SortOptions>(
    SortOptions.mostRelevant
  );

  // AI Preview state
  const [aiPreviewState, setAiPreviewState] = useState<AIPreviewState>(
    AIPreviewState.IDLE
  );
  const [aiPreviewVisible, setAiPreviewVisible] = useState<boolean>(false);
  const [aiPreviewResponse, setAiPreviewResponse] =
    useState<AIPreviewResponse | null>(null);
  const [aiPreviewError, setAiPreviewError] = useState<AIPreviewError | null>(
    null
  );
  const [aiPreviewSessionId, setAiPreviewSessionId] = useState<string | null>(
    null
  );

  // AI answering mode is CMS-gated: search page doctype > "Enable AI Mode".
  // "Enable Simple AI Mode" switches the overview on by itself and drops the
  // conversational half — a one-off answer whose Show More expands in place.
  // It wins when both flags are ticked.
  const aiSimpleMode = props.enableSimpleAiMode === true;
  const aiEnabled = aiSimpleMode || props.enableAiMode === true;
  const aiConversational = aiEnabled && !aiSimpleMode;

  // AI Mode: conversational view entered from the AI Overview's Show More.
  const [aiModeActive, setAiModeActive] = useState<boolean>(false);
  const [aiModeSeed, setAiModeSeed] = useState<AIModeSeed | null>(null);

  const getSort = (sortType: SortOptions) => {
    let sort;
    switch (sortType) {
      case SortOptions.newestOldest:
        sort = 'DefaultPublishedDateDesc';
        break;
      case SortOptions.mostRelevant:
        sort = 'Default';
        break;
      case SortOptions.oldestNewest:
        sort = 'DefaultPublishedDateAsc';
        break;
      default:
        sort = 'Default';
    }
    return sort;
  };

  const getFilter = (filter: string[] | null) => {
    const selectedFiltersWithoutCount = filter?.map((option) =>
      option.split('(')[0].trim()
    );
    const selectedFiltersString = selectedFiltersWithoutCount?.join(',');
    return selectedFiltersString;
  };

  // AI Preview methods
  const triggerAIPreview = async (query: string): Promise<void> => {
    if (!aiEnabled || !query.trim()) {
      hideAIPreview();
      return;
    }

    try {
      // A fresh search always returns to the results view.
      setAiModeActive(false);
      setAiModeSeed(null);

      // Generate or reuse the session ID BEFORE making the preview visible:
      // these setters flush unbatched (async function), and the AIPreview
      // component fires its request on the render where it becomes visible —
      // if the session id lands later, the preview streams on a throwaway
      // fallback id and AI Mode follow-ups lose the conversation context.
      const sessionId =
        aiPreviewSessionId ||
        (typeof window !== 'undefined'
          ? `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : 'ssr-session');
      if (!aiPreviewSessionId) {
        setAiPreviewSessionId(sessionId);
      }

      setAiPreviewState(AIPreviewState.LOADING);
      setAiPreviewVisible(true);
      setAiPreviewError(null);

      // AI preview will be handled by the AIPreview component itself
      // This method just manages the visibility and state in the store
      setAiPreviewState(AIPreviewState.IDLE);
    } catch (error) {
      const aiError: AIPreviewError = {
        type: 'network',
        message: 'Failed to initialize AI preview',
        details: error instanceof Error ? error.message : 'Unknown error',
        isRetryable: true,
      };
      setAiPreviewError(aiError);
      setAiPreviewState(AIPreviewState.ERROR);
    }
  };

  const hideAIPreview = (): void => {
    setAiPreviewVisible(false);
    setAiPreviewState(AIPreviewState.IDLE);
  };

  const clearAIPreviewError = (): void => {
    setAiPreviewError(null);
    if (aiPreviewState === AIPreviewState.ERROR) {
      setAiPreviewState(AIPreviewState.IDLE);
    }
  };

  const resetAIPreview = (): void => {
    setAiPreviewState(AIPreviewState.IDLE);
    setAiPreviewVisible(false);
    setAiPreviewResponse(null);
    setAiPreviewError(null);
    setAiPreviewSessionId(null);
    setAiModeActive(false);
    setAiModeSeed(null);
  };

  const openAIMode = (seed?: AIModeSeed | null): void => {
    if (!aiConversational) return;
    if (seed) {
      setAiModeSeed(seed);
    }
    setAiModeActive(true);
  };

  // Re-enter AI Mode from the tab row without replacing the seed/thread.
  const resumeAIMode = (): void => {
    if (!aiConversational) return;
    setAiModeActive(true);
  };

  const closeAIMode = (): void => {
    setAiModeActive(false);
  };

  // Keep the latest completed overview so the AI Mode tab can seed the
  // conversation even when entered without clicking Show More.
  const completeAIPreview = (response: AIPreviewResponse): void => {
    setAiPreviewResponse(response);
    setAiPreviewState(AIPreviewState.COMPLETE);
  };

  const searchData = async (
    query: string,
    filter: string[] | null,
    page: string | null,
    sort: SortOptions,
    newPage: boolean
  ): Promise<ResultsData> => {
    try {
      setIsSearchLoading(true);
      const getSortType = getSort(sort);
      const getFilters = getFilter(filter);
      const content = await searchv2(query, getFilters || null, page || null);
      setContentSearchResults(content);
      setSearchTerm(query);
      setSelectedFilters(filter);
      setSelectedPage(page);
      setSelectedSort(sort);
      setIsNewPage(newPage);
      return content;
    } catch (error) {
      throw new Error('Error fetching data');
    } finally {
      setIsSearchLoading(false);
    }
  };

  const getSuggestions = async (query: string): Promise<string[]> => {
    const removeHtmlTags = (str: string): string => {
      return str.replace(/<[^>]*>/g, '');
    };

    try {
      const suggestionContent = await searchSuggestion(query);
      let suggestions: string[] = [];

      if (suggestionContent && suggestionContent.length > 0) {
        suggestions = suggestionContent.map((content) => {
          const cleanedContent = removeHtmlTags(content);
          const trimmedContent = cleanedContent.slice(0, 60);
          return trimmedContent;
        });
      } else {
        const content = await searchv2(query, null, null);
        if (content && content.Hits) {
          suggestions = content.Hits.slice(0, 10)
            .map((hit) => hit.Heading)
            .filter((heading) => typeof heading === 'string')
            .map((heading) => {
              const pipeIndex = heading?.indexOf('|');
              const trimmedHeading =
                pipeIndex !== -1
                  ? heading?.slice(0, pipeIndex).trim()
                  : heading || '';
              const truncateHeading = trimmedHeading
                ? trimmedHeading.slice(0, 60)
                : '';
              return truncateHeading;
            });
        }
      }
      return suggestions;
    } catch (error) {
      throw new Error('Error fetching suggestions');
    } finally {
      setIsSearchLoading(false);
    }
  };

  return (
    <SearchProvider.Provider
      value={{
        searchTerm,
        selectedFilters,
        selectedPage,
        selectedSort,
        contentSearchResults,
        isSearchLoading,
        searchData,
        isNewPage,
        getSuggestions,
        adsData: props,

        // AI Preview state and methods
        aiPreviewState,
        aiPreviewVisible,
        aiPreviewResponse,
        aiPreviewError,
        aiPreviewSessionId,
        triggerAIPreview,
        hideAIPreview,
        clearAIPreviewError,
        resetAIPreview,

        // AI Mode
        aiEnabled,
        aiConversational,
        aiModeActive,
        aiModeSeed,
        openAIMode,
        resumeAIMode,
        closeAIMode,
        completeAIPreview,
      }}
    >
      {props.children}
    </SearchProvider.Provider>
  );
};
